/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread
} from "lexical";

import { $applyNodeReplacement, DecoratorNode } from "lexical";
import * as React from "react";
import { Suspense } from "react";

const DocumentComponent = React.lazy(
    // @ts-ignore
    () => import("./DocumentComponent")
);

export interface DocumentPayload {
    uuid: string;
    title: string;
    key?: NodeKey;
}

function convertDocumentElement(domNode: Node): null | DOMConversionOutput {
    if (domNode instanceof HTMLHeadingElement) {
        const { textContent: text } = domNode;
        const node = $createDocumentNode({ title: text, uuid: "" });
        return { node };
    }
    return null;
}

export type SerializedDocumentNode = Spread<
    {
        uuid: string;
        title: string;
    },
    SerializedLexicalNode
>;

export class DocumentNode extends DecoratorNode<JSX.Element> {
    __uuid: string;
    __title: string;

    static getType(): string {
        return "document";
    }

    static clone(node: DocumentNode): DocumentNode {
        return new DocumentNode(node.__uuid, node.__title, node.__key);
    }

    static importJSON(serializedNode: SerializedDocumentNode): DocumentNode {
        const { uuid, title } = serializedNode;
        const node = $createDocumentNode({ uuid, title });
        return node;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement("h1");
        element.setAttribute("uuid", this.__uuid);
        element.textContent = this.__title;
        return { element };
    }

    static importDOM(): DOMConversionMap | null {
        return {
            h1: () => ({
                conversion: convertDocumentElement,
                priority: 0
            })
        };
    }

    constructor(uuid: string, title: string, key?: NodeKey) {
        super(key);
        this.__uuid = uuid;
        this.__title = title;
    }

    exportJSON(): SerializedDocumentNode {
        return {
            uuid: this.getUuid(),
            title: this.getTitle(),
            type: "document",
            version: 1
        };
    }

    // View

    createDOM(config: EditorConfig): HTMLElement {
        const span = document.createElement("span");
        span.className = "editor-document";
        return span;
    }

    updateDOM(): false {
        return false;
    }

    getUuid(): string {
        return this.__uuid;
    }

    getTitle(): string {
        return this.__title;
    }

    getTextContent(): string {
        return "@@[" + this.__uuid + "]";
    }

    decorate(): JSX.Element {
        return (
            <Suspense fallback={null}>
                <DocumentComponent uuid={this.__uuid} title={this.__title} nodeKey={this.getKey()} />
            </Suspense>
        );
    }
}

export function $createDocumentNode({ uuid, title, key }: DocumentPayload): DocumentNode {
    return $applyNodeReplacement(new DocumentNode(uuid, title, key));
}

export function $isDocumentNode(node: LexicalNode | null | undefined): node is DocumentNode {
    return node instanceof DocumentNode;
}
